import { BaseScene } from './BaseScene';
import { UserProfile, equipEarth, equipTower, equipPet, equipEquipment, unequipEquipment, combineEquipment, getEquipmentManager } from '../entities/base/UserProfile';
import { EquipmentType, RARITY_COLORS, getEquipmentsByType } from '../data/EquipmentData';
import { Equipment } from '../entities/equipment/Equipment';
import { t } from '../i18n/lang';

type InventoryTab = 'earth' | 'tower' | 'pet' | 'equipment';

export class InventoryScene extends BaseScene {
  private userProfile: UserProfile;
  private currentTab: InventoryTab = 'equipment';
  private contentContainer: Phaser.GameObjects.Container;
  private selectedEquipments: string[] = [];
  private filterType: EquipmentType = EquipmentType.WEAPON;

  constructor() {
    super({ key: 'Inventory' });
  }

  override create(): void {
    super.create();
    this.userProfile = (window as any).userProfile;
    this.createHeader();
    this.createTabNavigation();
    this.createContentArea();
    this.createEquippedEarthAndSlots();
    this.showTabContent(this.currentTab);
  }

  private createHeader(): void {
    const width = this.cameras.main.width;
    this.add.text(width / 2, 50, 'INVENTORY', {
      fontSize: '36px', fontFamily: 'Arial Black', color: '#4CAF50', fontStyle: 'bold'
    }).setOrigin(0.5);
    // Back button
    const backBtn = this.add.text(60, 50, '← BACK', {
      fontSize: '18px', fontFamily: 'Arial Black', color: '#fff', backgroundColor: '#888', padding: { x: 12, y: 6 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => this.scene.start('MainMenu'));
    backBtn.on('pointerover', () => backBtn.setStyle({ backgroundColor: '#555' }));
    backBtn.on('pointerout', () => backBtn.setStyle({ backgroundColor: '#888' }));
  }

  private createTabNavigation(): void {
    const width = this.cameras.main.width;
    const tabY = 120;
    const tabs: { key: InventoryTab, name: string, icon: string, color: number }[] = [
      { key: 'earth', name: 'EARTH', icon: '🌍', color: 0x27ae60 },
      { key: 'tower', name: 'TOWERS', icon: '🏗️', color: 0x3498db },
      { key: 'pet', name: 'PETS', icon: '🐾', color: 0x9b59b6 },
      { key: 'equipment', name: 'EQUIPMENT', icon: '⚔️', color: 0xf39c12 }
    ];
    const tabWidth = (width - 40) / tabs.length;
    tabs.forEach((tab, index) => {
      const tabX = 20 + (index * tabWidth) + (tabWidth / 2);
      const tabBtn = this.add.text(tabX, tabY, `${tab.icon} ${tab.name}`, {
        fontSize: '18px', fontFamily: 'Arial Black', color: this.currentTab === tab.key ? '#4CAF50' : '#fff', backgroundColor: this.currentTab === tab.key ? '#444' : '#222', padding: { x: 18, y: 8 }
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      tabBtn.on('pointerdown', () => {
        this.currentTab = tab.key;
        this.showTabContent(tab.key);
        // Update tab style
        this.children.list.forEach((child: any) => {
          if (child.text && tabs.some(t => `${t.icon} ${t.name}` === child.text)) {
            child.setStyle({ color: child.text.startsWith(tab.icon) ? '#4CAF50' : '#fff', backgroundColor: child.text.startsWith(tab.icon) ? '#444' : '#222' });
          }
        });
      });
    });
  }

  private createContentArea(): void {
    this.contentContainer = this.add.container(0, 160);
  }

  private showTabContent(tab: InventoryTab): void {
    this.contentContainer.removeAll(true);
    switch (tab) {
      case 'earth':
        this.createEarthInventory();
        break;
      case 'tower':
        this.createTowerInventory();
        break;
      case 'pet':
        this.createPetInventory();
        break;
      case 'equipment':
        this.createEquipmentInventory();
        break;
    }
  }

  private createEarthInventory(): void {
    // Hiển thị các Earth đã sở hữu, cho phép chọn để trang bị
    // ... (tùy chỉnh theo userProfile.ownedEarths)
    // Đơn giản: danh sách text, nút Equip
  }

  private createTowerInventory(): void {
    // Hiển thị các Tower đã sở hữu, cho phép chọn để trang bị
    // ... (tùy chỉnh theo userProfile.ownedTowers)
  }

  private createPetInventory(): void {
    // Hiển thị các Pet đã sở hữu, cho phép chọn để trang bị
    // ... (tùy chỉnh theo userProfile.ownedPets)
  }

  private createEquipmentInventory(): void {
    // Filter type tabs
    this.createEquipmentTypeTabs();
    this.displayEquipmentGrid(this.filterType);
  }

  private createEquippedEarthAndSlots(): void {
    // Xóa cũ nếu có
    if (this.contentContainer) this.contentContainer.removeAll(true);
    const centerX = this.cameras.main.centerX;
    const centerY = 340;
    // Earth (icon)
    const earthIcon = this.add.text(centerX, centerY, '🌍', {
      fontSize: '90px', fontFamily: 'Arial', color: '#27ae60'
    }).setOrigin(0.5);
    this.contentContainer.add(earthIcon);
    // Slot positions (theo kiểu đồng hồ)
    const slotDefs = [
      { type: EquipmentType.WEAPON, x: 0, y: -110, label: '⚔️' },
      { type: EquipmentType.ARMOR, x: 0, y: 110, label: '🛡️' },
      { type: EquipmentType.ACCESSORY, x: -110, y: 0, label: '💍' },
      { type: EquipmentType.SPECIAL, x: 110, y: 0, label: '✨' },
    ];
    const equipped = this.userProfile.equippedItems || {};
    slotDefs.forEach(slot => {
      // Vị trí slot quanh Earth
      const slotX = centerX + slot.x;
      const slotY = centerY + slot.y;
      // Nếu có item đang trang bị
      const item = Object.values(equipped).find((e: any) => e && e.type === slot.type);
      let icon = slot.label;
      let color = '#fff';
      if (item) {
        icon = item.icon || slot.label;
        color = '#FFD700';
      }
      const slotBtn = this.add.text(slotX, slotY, icon, {
        fontSize: '40px', fontFamily: 'Arial', color,
        backgroundColor: item ? '#222' : '#444',
        padding: { x: 10, y: 10 },
        // borderRadius: 20
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      slotBtn.on('pointerdown', () => {
        if (item) {
          unequipEquipment(this.userProfile, item.instanceId);
          (window as any).userProfile = this.userProfile;
          this.createEquippedEarthAndSlots();
          this.displayEquipmentGrid(this.filterType);
        }
      });
      slotBtn.on('pointerover', () => slotBtn.setStyle({ backgroundColor: '#666' }));
      slotBtn.on('pointerout', () => slotBtn.setStyle({ backgroundColor: item ? '#222' : '#444' }));
      this.contentContainer.add(slotBtn);
    });
  }

  private createEquipmentTypeTabs(): void {
    const types = [
      { type: EquipmentType.WEAPON, name: 'WEAPONS' },
      { type: EquipmentType.ARMOR, name: 'ARMOR' },
      { type: EquipmentType.ACCESSORY, name: 'ACCESSORIES' },
      { type: EquipmentType.SPECIAL, name: 'SPECIAL' },
    ];
    const tabWidth = 120;
    const startX = this.cameras.main.centerX - (types.length * tabWidth) / 2;
    types.forEach((typeInfo, index) => {
      const x = startX + index * tabWidth;
      const isActive = this.filterType === typeInfo.type;
      const tabBtn = this.add.text(x, 200, typeInfo.name, {
        fontSize: '14px',
        color: isActive ? '#4CAF50' : '#ffffff',
        backgroundColor: isActive ? '#444444' : '#222222',
        padding: { x: 15, y: 8 },
        fontFamily: 'Arial',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      tabBtn.on('pointerdown', () => {
        this.filterType = typeInfo.type;
        this.displayEquipmentGrid(typeInfo.type);
        // Update tab colors
        this.contentContainer.list.forEach((child: any) => {
          if (child.text && types.some(t => t.name === child.text)) {
            child.setStyle({ 
              color: child.text === typeInfo.name ? '#4CAF50' : '#ffffff',
              backgroundColor: child.text === typeInfo.name ? '#444444' : '#222222'
            });
          }
        });
      });
      this.contentContainer.add(tabBtn);
    });
  }

  private displayEquipmentGrid(type: EquipmentType): void {
    // Xóa các item cũ (nhưng giữ Earth + slot)
    this.contentContainer.list = this.contentContainer.list.filter((child: any) => {
      // Giữ Earth và slot (y ~ 340)
      if (child.y && Math.abs(child.y - 340) < 150) return true;
      if (child.y > 240) { child.destroy(); return false; }
      return true;
    });
    const equipmentManager = getEquipmentManager();
    const ownedEquipments = equipmentManager.getEquipmentsByType(type);
    // Hiển thị dạng grid
    const gridX = this.cameras.main.centerX - 320;
    const gridY = 540;
    const cellW = 120, cellH = 120, cols = 5;
    ownedEquipments.forEach((equipment, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = gridX + col * cellW;
      const y = gridY + row * cellH;
      this.createEquipmentGridItem(equipment, x, y);
    });
    // Combine section nếu đủ 3 item
    if (ownedEquipments.length >= 3) {
      this.createCombineSection(type, gridY + Math.ceil(ownedEquipments.length / cols) * cellH + 20);
    }
  }

  private createEquipmentGridItem(equipment: Equipment, x: number, y: number): void {
    // Ô nền
    const bgColor = equipment.instance.isEquipped ? 0x4CAF50 : 0x444444;
    const bg = this.add.rectangle(x, y, 110, 110, bgColor, 0.85);
    this.contentContainer.add(bg);
    // Tên + level
    const nameText = this.add.text(x - 45, y - 30, equipment.getDisplayName(), {
      fontSize: '13px', color: RARITY_COLORS[equipment.config.rarity], fontFamily: 'Arial'
    }).setOrigin(0, 0.5);
    this.contentContainer.add(nameText);
    // Tooltip khi hover/click
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => {
      const stats = equipment.getCurrentStats();
      const tooltip = this.add.text(x, y + 60, this.getStatsPreview(stats), {
        fontSize: '12px', color: '#fff', backgroundColor: '#222', padding: { x: 8, y: 4 }, fontFamily: 'Arial'
      }).setOrigin(0.5);
      bg.setData('tooltip', tooltip);
      this.contentContainer.add(tooltip);
    });
    bg.on('pointerout', () => {
      const tooltip = bg.getData('tooltip');
      if (tooltip) tooltip.destroy();
    });
    // Equip/Unequip button
    const equipBtn = this.add.text(x, y + 35, equipment.instance.isEquipped ? 'UNEQUIP' : 'EQUIP', {
      fontSize: '12px', color: '#fff', backgroundColor: equipment.instance.isEquipped ? '#ff6666' : '#2196F3', padding: { x: 10, y: 4 }, fontFamily: 'Arial'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    equipBtn.on('pointerdown', () => {
      if (!equipment.instance.isEquipped) {
        equipEquipment(this.userProfile, equipment.instance.instanceId);
        (window as any).userProfile = this.userProfile;
        this.createEquippedEarthAndSlots();
        this.displayEquipmentGrid(equipment.config.type);
      } else {
        unequipEquipment(this.userProfile, equipment.instance.instanceId);
        (window as any).userProfile = this.userProfile;
        this.createEquippedEarthAndSlots();
        this.displayEquipmentGrid(equipment.config.type);
      }
    });
    this.contentContainer.add(equipBtn);
    // Chọn để combine
    const isSelected = this.selectedEquipments.includes(equipment.instance.instanceId);
    const selectBtn = this.add.text(x + 35, y - 35, isSelected ? '\u2611' : '\u2610', {
      fontSize: '16px', color: isSelected ? '#4CAF50' : '#fff', fontFamily: 'Arial'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    selectBtn.on('pointerdown', () => {
      this.toggleEquipmentSelection(equipment.instance.instanceId);
      this.displayEquipmentGrid(equipment.config.type);
    });
    this.contentContainer.add(selectBtn);
  }

  private createCombineSection(type: EquipmentType, y: number): void {
    const centerX = this.cameras.main.centerX;
    const combineTitle = this.add.text(centerX, y, 'COMBINE (Select 3 same equipment)', {
      fontSize: '16px', color: '#9966ff', fontFamily: 'Arial'
    }).setOrigin(0.5);
    this.contentContainer.add(combineTitle);
    const selectedCount = this.selectedEquipments.length;
    const canCombine = selectedCount === 3;
    const combineBtn = this.add.text(centerX, y + 30, `COMBINE (${selectedCount}/3)`, {
      fontSize: '14px', color: '#fff', backgroundColor: canCombine ? '#9966ff' : '#666', padding: { x: 15, y: 8 }, fontFamily: 'Arial'
    }).setOrigin(0.5);
    if (canCombine) {
      combineBtn.setInteractive({ useHandCursor: true });
      combineBtn.on('pointerdown', () => {
        if (this.selectedEquipments.length === 3) {
          const success = combineEquipment(
            this.userProfile, 
            this.selectedEquipments[0] as string, 
            this.selectedEquipments[1] as string, 
            this.selectedEquipments[2] as string
          );
          if (success) {
            this.selectedEquipments = [];
            (window as any).userProfile = this.userProfile;
            this.displayEquipmentGrid(type);
          }
        }
      });
      combineBtn.on('pointerover', () => combineBtn.setStyle({ backgroundColor: '#7b4fb3' }));
      combineBtn.on('pointerout', () => combineBtn.setStyle({ backgroundColor: '#9966ff' }));
    }
    this.contentContainer.add(combineBtn);
  }

  private toggleEquipmentSelection(instanceId: string): void {
    const index = this.selectedEquipments.indexOf(instanceId);
    if (index >= 0) {
      this.selectedEquipments.splice(index, 1);
    } else if (this.selectedEquipments.length < 3) {
      this.selectedEquipments.push(instanceId);
    }
  }

  private getStatsPreview(stats: any): string {
    return Object.entries(stats).map(([k, v]) => `${k}: ${v}`).join('\n');
  }
} 